<template>
  <v-content class="pa-0 ma-0">
    <v-dialog
      v-model="createNoteDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="800"
      persistent
      @keydown.esc="createNoteDialogOpened = false"
    >
      <v-card>
        <v-card-title class="title pb-0">
          Create New Note
          <v-spacer />
          <v-tooltip bottom>
            <v-switch
              slot="activator"
              class="default-input my-0 py-0"
              solo
              hide-details
              label="Create for Client"
              v-model="clientNote"
            />
            <span>This note will be shared for all the clients' entities</span>
          </v-tooltip>
        </v-card-title>
        <v-card-text>
          <v-layout column class="text-xs-left">
            <v-flex>
              <div class="darkgrey--text">Title</div>
              <v-text-field
                v-model="newTitle"
                class="default-input"
                solo
                placeholder="Optional"
              />
            </v-flex>
            <v-flex>
              <div class="darkgrey--text">Body</div>
              <v-textarea
                v-model="newText"
                class="default-input"
                solo
                :rules="existenceRule"
                placeholder="New Note"
              />
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer class="hidden-xs-only" />
          <v-btn
            :block="$vuetify.breakpoint.xsOnly"
            class="default-input primary text-capitalize"
            @click="createNote()"
            :loading="noteSaving"
            :disabled="isEmpty(newText)"
          >
            Save
          </v-btn>
          <v-btn
            :block="$vuetify.breakpoint.xsOnly"
            outline
            class="default-input error--text text-capitalize"
            @click="createNoteDialogOpened = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card>
      <v-card-title class="title">
        Notes
        <v-spacer />
        <soraban-action-button
          @click="createNoteDialogOpened = true"
          icon="add"
          help="Create Note"
          color="primary"
        />
        <v-btn
          fab
          small
          class="error--text text-none my-0"
          flat
          @click="$emit('closeDialog', true)"
        >
          <v-icon size="25">clear</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text v-if="!noteLoading" class="lighten-3 text-xs-left pa-2">
        <v-layout wrap align-start v-if="!isEmpty(notes)">
          <v-flex
            shrink
            v-for="(note, index) in notes"
            :key="index"
            class="pa-2"
          >
            <v-tooltip bottom>
              <v-card
                flat
                style="min-width: 200px; min-height: 120px; max-width: 300px"
                class="note__card"
                slot="activator"
              >
                <v-card-title
                  class="subheading font-weight-bold text-xs-left py-2 pl-2"
                  v-if="!isEmpty(note.title)"
                >
                  <v-layout align-center>
                    <v-chip v-if="note.client_note" small class="my-0 ml-0 mr-2"
                      >Client</v-chip
                    ><span>{{ note.title }}</span>
                    <v-spacer />
                    <v-icon
                      @click="deleteNote(note.id, index)"
                      class="ml-2"
                      size="18"
                    >
                      clear
                    </v-icon>
                  </v-layout>
                </v-card-title>
                <v-divider />
                <v-card-text class="pt-1 pl-1">
                  <v-layout
                    class="pt-2"
                    align-center
                    v-if="isEmpty(note.title)"
                  >
                    <v-chip v-if="note.client_note" small class="my-0 ml-0 mr-2"
                      >Client</v-chip
                    ><span>{{ note.title }}</span>
                    <v-spacer />
                    <v-icon
                      @click="deleteNote(note.id, index)"
                      class="ml-2"
                      size="18"
                    >
                      clear
                    </v-icon>
                  </v-layout>
                  <v-layout wrap class="pa-2">
                    {{ note.text }}
                  </v-layout>
                </v-card-text>
              </v-card>
              <span
                >Added by {{ note.user_name }} on
                {{ moment(note.created_at).format('ll') }}</span
              >
            </v-tooltip>
          </v-flex>
        </v-layout>
        <v-alert
          :value="true"
          outline
          block
          :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
          color="info"
          class="text-xs-left mb-2"
          v-else
        >
          No notes exist for this client or entity.
        </v-alert>
      </v-card-text>
      <v-card-text v-else class="grey lighten-3">
        <v-progress-circular
          indeterminate
          size="50"
          class="primary--text ma-5"
        />
      </v-card-text>
    </v-card>
  </v-content>
</template>
<script>
import API from '@/api';
import { mapState, mapGetters } from 'vuex';
import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
export default {
  name: 'InternalNote',
  props: {
    currentCompanyId: {
      required: true,
      type: Number,
    },
    dialogOpened: {
      required: true,
      type: Boolean,
    },
  },
  data() {
    return {
      notes: [],
      createNoteDialogOpened: false,
      newTitle: '',
      newText: '',
      clientNote: false,
      existenceRule: [v => !!v || 'This field is required'],
      noteSaving: false,
      noteLoading: false,
    };
  },
  methods: {
    isEmpty,
    moment,
    async getInternalNotes() {
      this.noteLoading = true;
      const response = await API.note.getInternalNotes(this.currentCompanyId);
      if (response.data.error) {
        console.log(response.data.error);
        this.$store.dispatch('application/setErrorNotification', {
          message: response.data.error,
        });
      } else {
        this.notes = response.data;
      }
      this.noteLoading = false;
    },
    async createNote() {
      try {
        this.noteSaving = true;
        const response = await API.note.createNote({
          companyId: this.currentCompanyId,
          title: this.newTitle,
          text: this.newText,
          clientNote: this.clientNote,
        });
        if (response.data.error) {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: `Note has been saved successfully`,
          });
          this.newTitle = '';
          this.newText = '';
          this.clientNote = false;
          this.notes.push(response.data);
          this.createNoteDialogOpened = false;
          this.$emit('get-current-company');
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Something went wrong while saving information.',
        });
      } finally {
        this.noteSaving = false;
      }
    },
    async deleteNote(id, index) {
      try {
        const response = await API.note.deleteNote(id);
        if (response.data.error) {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: `Note has been deleted`,
          });
          this.notes.splice(index, 1);
          this.$emit('get-current-company');
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Something went wrong while saving information.',
        });
      }
    },
  },
  watch: {
    dialogOpened: {
      async handler(value) {
        this.getInternalNotes();
      },
      immediate: true,
    },
  },
};
</script>
<style>
.note__card {
  background-color: #e9f4fd !important;
  border-color: #e9f4fd !important;
}
.frame {
  overflow: auto;
  max-height: 700px;
}

.frame::-webkit-scrollbar {
  -webkit-appearance: none;
}

.frame::-webkit-scrollbar:vertical {
  width: 11px;
}

.frame::-webkit-scrollbar:horizontal {
  height: 11px;
}

.frame::-webkit-scrollbar-thumb {
  border-radius: 8px;
  border: 2px solid white; /* should match background, can't be transparent */
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
